<template>
  <NTable
    v-show="viewMode?.value === 0"
    ref="tableRef"
    :data="data"
    :column-splitter="false"
    :striped="false"
    cell-padding="small"
    :sort-by="sortBy"
    :highlight-by="{
      prop: 'isin',
      value: activeIsin,
    }"
    fixed-header
    :class="[
      'scrollbar max-h-full !h-auto',
      {
        'min-w-[6rem]': viewMode?.value === 0,
      },
    ]"
    @click.stop
    @row-click="undefined"
    @scroll="onScrollHandler"
    @update:sort-by="emit('update:sort', $event)"
  >
    <NTableColumn
      v-slot="{ row }"
      :label="t('option.strikePrice')"
      class="p-[0.58rem] w-[6rem]"
      prop="strikePrice"
      sortable
      align="text-center"
    >
      <NumberFormatter #="{ number }" :value="row.strikePrice" round>
        <NText weight="bold" variant="caption2" dir="ltr">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import NumberFormatter from '@/components/Number.vue';

import { TViewMode } from './index.vue';
import { useActiveSymbolIsin } from '@/modules/symbol';

type TItem = {
  strikePrice: number;
};

interface TProps {
  data: TItem[];
  viewMode: TViewMode;
  sortBy?: unknown;
}

type TScroll = {
  top: number;
  left: number;
};

type TEmits = {
  (e: 'scroll', value: TScroll): void;
  (e: 'update:sort', value: Record<string, any>): void;
};

defineProps<TProps>();
const emit = defineEmits<TEmits>();

const tableRef = useTemplateRef('tableRef');

const { t } = useI18n();
const { activeIsin } = useActiveSymbolIsin();

function onScrollHandler(event: Event) {
  emit('scroll', {
    top: (event.target as Element).scrollTop,
    left: -(event.target as Element).scrollLeft,
  });
}

defineExpose({ tableRef });
</script>

<style lang="css" scoped>
.scrollbar {
  @apply border-0 border-x border-neutral-secondary;
  border-radius: unset;
  box-shadow:
    2px 2px 6px 0px rgba(0, 0, 0, 0.05),
    -2px 0px 6px 0px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.scrollbar::-webkit-scrollbar,
.scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  width: 0;
  height: 0;
}
</style>
